/* SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved. */
/* SPDX-License-Identifier: Apache-2.0 */

:host {
  --nve-sys-interaction-background: var(--nve-sys-interaction-background-200);
  --nve-sys-interaction-field-background: var(--nve-sys-interaction-background-200);
  --background: var(--nve-sys-layer-container-background);
  --color: var(--nve-sys-layer-canvas-color);
  --padding: 0 var(--nve-ref-size-300);
  --min-height: var(--nve-ref-size-1000);
  --border-radius: var(--nve-ref-border-radius-sm);
  --gap: var(--nve-ref-space-xs);
  --border-bottom: 0;
  --box-shadow: var(--nve-ref-shadow-100);
  --width: 100%;
  width: var(--width);
  contain: initial;
}

:host([container='flat']),
:host([container='inset']) {
  --nve-sys-interaction-background: var(--nve-sys-interaction-background-100);
  --nve-sys-interaction-field-background: var(--nve-sys-interaction-background-100);
  --background: transparent;
  --border-bottom: var(--nve-ref-border-width-md) solid var(--nve-ref-border-color-muted);
  --box-shadow: none;
  --border-radius: 0;
  --padding: 0;
}

:host([container='full']) {
  --box-shadow: none;
  --border-radius: 0;
}

:host([container='inset']) {
  --border-bottom: 0;
}

:host([container='flat']:focus-within) {
  --border-bottom: var(--nve-ref-border-width-md) solid var(--nve-ref-border-color-emphasis);
}

:host([status='accent']) {
  --background: var(--nve-sys-support-accent-color);
  --color: var(--nve-sys-text-white-color);
}

:host([status='accent']) ::slotted(nve-button),
:host([status='accent']) ::slotted(nve-icon-button),
:host([status='accent']) nve-icon-button {
  --color: var(--nve-sys-text-white-color) !important;
  --_icon-color: var(--nve-sys-text-white-color) !important;
}

:host([status='accent']) ::slotted([nve-text]) {
  color: var(--nve-sys-text-white-color) !important;
}

[internal-host] {
  background: var(--background);
  color: var(--color);
  padding: var(--padding);
  min-height: var(--min-height);
  border-radius: var(--border-radius);
  border-bottom: var(--border-bottom);
  box-shadow: var(--box-shadow);
  width: var(--width);
  gap: var(--gap);
  display: flex;
  align-items: center;
}

:host([orientation='vertical']) {
  --padding: var(--nve-ref-size-100) 0;
  --gap: var(--nve-ref-space-xs);
  --width: fit-content;
}

:host([orientation='vertical']) [internal-host],
:host([orientation='vertical']) slot {
  flex-direction: column;
  align-items: center;
  margin: 0;
}

:host([orientation='vertical']) ::slotted(nve-divider) {
  width: 100%;
}

[name='prefix'],
[name='suffix'] {
  display: none;
  gap: var(--gap);
  align-items: center;
  justify-content: start;
}

[name='suffix'] {
  justify-content: end;
}

:host(:state(has-prefix)) [name='prefix'],
:host(:state(has-suffix)) [name='suffix'] {
  display: flex;
}

::slotted(nve-combobox),
::slotted(nve-search),
::slotted(nve-input) {
  --border-bottom: 0;
  min-width: 20cqw;
  flex: 1;
}

::slotted(nve-select) {
  --border-bottom: 0;
}

slot:not([name]) {
  gap: var(--gap);
  width: 100%;
  display: flex;
  align-items: center;
  text-wrap: nowrap;
  overflow: auto hidden;
  scrollbar-color: var(--nve-sys-scrollbar-thumb-color) var(--nve-sys-scrollbar-track-color);
  scrollbar-width: var(--nve-sys-scrollbar-width);
}

:host([content='wrap']) slot:not([name]) {
  gap: var(--gap);
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  text-wrap: balance;
  overflow-x: initial;
}

:host(:state(scrollbar)) slot:not([name]) {
  /* stylelint-disable-next-line declaration-property-value-disallowed-list */
  margin-block-end: -14px;
}

::slotted([nve-text]),
::slotted(a),
::slotted(span) {
  text-wrap: nowrap !important;
}
